import React, {useContext} from 'react';
import styles from './create-form.module.scss';
import * as Label from '@radix-ui/react-label';
import classNames from 'classnames';
import {Controller, FieldErrors, useForm} from 'react-hook-form';
import Yup from 'src/infra/Yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {PlatformIntl} from 'src/infra/PlatformIntl';
import {MaintenanceDispatchContext} from './contexts';
import {useCreateMaintenance} from './queries';
import {Field} from 'src/autobahn/components/form/field';
import {TextInput} from 'src/autobahn/components/input/text-input';
import {NumberInput} from 'src/autobahn/components/input/number-input';
import {DateInput} from 'src/autobahn/components/input/date-input';
import FormLayout from 'src/autobahn/layouts/form-layout';
import {useIntl} from 'react-intl';

type FormValues = {
  name: string;
  odometerValue?: number;
  hourmeterValue?: number;
  dateValue?: Date | null;
  intervalValue?: number;
}

const _intl = PlatformIntl.getInstance();

const schema = Yup.object({
  name: Yup.string().label(_intl.formatMessage('sharedTitle')).required(),
  odometerValue: Yup.number()
    .transform((value) => isNaN(value) ? undefined : value)
    .min(0, _intl.formatMessage('sharedMinZero'))
    .label(_intl.formatMessage('sharedOdometer'))
    .typeError(_intl.formatMessage('sharedInvalidNumber'))
    .notRequired(),
  hourmeterValue: Yup.number()
    .transform((value) => isNaN(value) ? undefined : value)
    .min(0, _intl.formatMessage('sharedMinZero'))
    .label(_intl.formatMessage('sharedHourmeter'))
    .typeError(_intl.formatMessage('sharedInvalidNumber'))
    .notRequired(),
  intervalValue: Yup.number()
    .transform((value) => isNaN(value) ? undefined : value)
    .min(0, _intl.formatMessage('sharedMinZero'))
    .label(_intl.formatMessage('sharedInterval'))
    .typeError(_intl.formatMessage('sharedInvalidNumber'))
    .notRequired(),
  dateValue: Yup.date()
    .label(_intl.formatMessage('sharedDate'))
    .typeError(_intl.formatMessage('sharedDateInvalid'))
    .notRequired(),
});

const MaintenanceCreateForm = () => {
  const dispatch = useContext(MaintenanceDispatchContext);
  const {mutate: createMaintenance} = useCreateMaintenance();

  const intl = useIntl();

  const {control, register, handleSubmit} = useForm<FormValues>({
    resolver: yupResolver(schema as unknown as Yup.ObjectSchema<FormValues>),
    defaultValues: {
      name: '',
    },
  });

  const onSubmitSuccess = ({dateValue, ...data}: FormValues) => {
    console.log('__form_success', data);
    createMaintenance({
      ...data,
      dateValue: dateValue ? dateValue.toISOString() : undefined,
      type: '',
    });
  };

  const onSubmitError = (data: FieldErrors<FormValues>) => {
    console.log('__form_error', data);
  };

  const cancel = () => {
    dispatch({type: 'SET_ACTIVE_VIEW', payload: 'list'});
  };

  return (
    <FormLayout
      onCancel={cancel}
      onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
      title={intl.formatMessage({id: 'sharedMaintenance'})}
    >
      <Field
        name='name'
        type='text'
        label={intl.formatMessage({id: 'sharedTitle'})}
        // @ts-ignore
        style={{justifyContent: 'space-between'}}
      >
        <TextInput
          {...register('name')}
        />
      </Field>

      <Label.Root asChild>
        <span className={classNames(styles.fieldLabel, styles.kindAndPeriodLabel)}>{intl.formatMessage({id: 'sharedKind'})} / {intl.formatMessage({id: 'sharedPeriod'})}</span>
      </Label.Root>
      <div style={{display: 'flex', flexDirection: 'column', rowGap: '16px', width: '50%'}}>
        <Field
          name='odometer'
          label={`${intl.formatMessage({id: 'sharedOdometer'})} (Km)`}
          labelKind='light'
          type='number'
          horizontal
          // @ts-ignore
          style={{justifyContent: 'space-between'}}
        >
          <NumberInput
            {...register('odometerValue')}
            placeholder={`${intl.formatMessage({id: 'sharedEvery'})} ...`}
          />
        </Field>

        <Field
          type='date'
          name='dateValue'
          label={intl.formatMessage({id: 'sharedDate'})}
          labelKind='light'
          horizontal
          // @ts-ignore
          style={{justifyContent: 'space-between'}}
        >
          <Controller
            control={control}
            name='dateValue'
            render={({field}) => (
              <DateInput
                {...field}
                displayFormat='dd/MM/yyyy'
                placeholder={`${intl.formatMessage({id: 'sharedDay'})} ...`}
              />
            )}
          />
        </Field>

        <Field
          name='hourmeterValue'
          label={`${intl.formatMessage({id: 'sharedHourmeter'})} (${intl.formatMessage({id: 'sharedHours'})})`}
          labelKind='light'
          type='number'
          horizontal
          // @ts-ignore
          style={{justifyContent: 'space-between'}}
        >
          <NumberInput
            {...register('hourmeterValue')}
            placeholder={`${intl.formatMessage({id: 'sharedEvery'})} ...`}
          />
        </Field>

        <Field
          name='intervalValue'
          label={`${intl.formatMessage({id: 'sharedInterval'})} (${intl.formatMessage({id: 'sharedMonths'})})`}
          labelKind='light'
          type='number'
          horizontal
          // @ts-ignore
          style={{justifyContent: 'space-between'}}
        >
          <NumberInput
            {...register('intervalValue')}
            placeholder={`${intl.formatMessage({id: 'sharedEvery'})} ...`}
          />
        </Field>
      </div>
    </FormLayout>
  );
};

export default MaintenanceCreateForm;
